/**
 * Utility functions for translating role names between English and Arabic
 */

import { useTranslation } from '../hooks/useTranslation';

/**
 * Translates a role name to the appropriate language
 * @param {string} role - The role name to translate
 * @param {string} language - The target language ('en' or 'ar')
 * @returns {string} - The translated role name
 */
export const translateRole = (role, language = 'en') => {
  if (!role) return '';
  
  const roleLower = role.toLowerCase().trim();
  
  // Role mapping for different languages
  const roleMappings = {
    en: {
      'maincontractor': 'Main Contractor',
      'main_contractor': 'Main Contractor',
      'main contractor': 'Main Contractor',
      'subcontractor': 'Sub Contractor', 
      'sub_contractor': 'Sub Contractor',
      'sub contractor': 'Sub Contractor',
      'engineer': 'Engineer',
      'contractor': 'Contractor',
      'inspection': 'Inspection',
      'quantification': 'Quantification',
      'consultantdesign': 'Consultant Design',
      'consultant design': 'Consultant Design',
      'consultant_design': 'Consultant Design'
    },
    ar: {
      'maincontractor': 'المقاول الرئيسي',
      'main_contractor': 'المقاول الرئيسي', 
      'main contractor': 'المقاول الرئيسي',
      'subcontractor': 'المقاول الفرعي',
      'sub_contractor': 'المقاول الفرعي',
      'sub contractor': 'المقاول الفرعي', 
      'engineer': 'مهندس',
      'contractor': 'مقاول',
      'inspection': 'التفتيش',
      'quantification': 'القياس',
      'consultantdesign': 'استشاري التصميم',
      'consultant design': 'استشاري التصميم',
      'consultant_design': 'استشاري التصميم'
    }
  };
  
  // Return translated role or original if not found
  return roleMappings[language]?.[roleLower] || role;
};

/**
 * Hook for translating role names that automatically updates when language changes
 * @param {string} role - The role name to translate
 * @returns {string} - The translated role name
 */
export const useTranslateRole = (role) => {
  const { language } = useTranslation();
  return translateRole(role, language);
};


/**
 * Translates an array of roles to the appropriate language
 * @param {string[]|string} roles - The role(s) to translate
 * @param {string} language - The target language ('en' or 'ar')
 * @returns {string[]|string} - The translated role(s)
 */
export const translateRoles = (roles, language = 'en') => {
  if (!roles) return '';
  
  if (Array.isArray(roles)) {
    return roles.map(role => translateRole(role, language));
  }
  
  return translateRole(roles, language);
};

/**
 * Checks if a role is a main contractor
 * @param {string} role - The role to check
 * @returns {boolean} - True if the role is a main contractor
 */
export const isMainContractor = (role) => {
  if (!role) return false;
  const roleLower = role.toLowerCase().trim();
  return roleLower === 'maincontractor' || 
         roleLower === 'main_contractor' || 
         roleLower === 'main contractor';
};

/**
 * Checks if a role is a sub contractor
 * @param {string} role - The role to check
 * @returns {boolean} - True if the role is a sub contractor
 */
export const isSubContractor = (role) => {
  if (!role) return false;
  const roleLower = role.toLowerCase().trim();
  return roleLower === 'subcontractor' || 
         roleLower === 'sub_contractor' || 
         roleLower === 'sub contractor';
};

/**
 * Checks if a role is an engineer
 * @param {string} role - The role to check
 * @returns {boolean} - True if the role is an engineer
 */
export const isEngineer = (role) => {
  if (!role) return false;
  const roleLower = role.toLowerCase().trim();
  return roleLower === 'engineer';
};

/**
 * Gets the role type category
 * @param {string} role - The role to categorize
 * @returns {string} - The role category ('main_contractor', 'sub_contractor', 'engineer', 'contractor', 'unknown')
 */
export const getRoleCategory = (role) => {
  if (isMainContractor(role)) return 'main_contractor';
  if (isSubContractor(role)) return 'sub_contractor';
  if (isEngineer(role)) return 'engineer';
  if (role && role.toLowerCase().includes('contractor')) return 'contractor';
  return 'unknown';
};
